import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import bookingService from '../../services/bookingService';
import { apiGet } from '../../lib/api';
import { useCurrency } from '../../context/CurrencyContext';
import { 
  Package, Clock, Calendar, CreditCard, CheckCircle, 
  ChevronRight, User, Coins, AlertCircle 
} from 'lucide-react';

export default function ServiceBookingWizard() {
  const { t } = useTranslation();
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // State
  const [service, setService] = useState(null);
  const [creative, setCreative] = useState(null);
  const [packages, setPackages] = useState([]);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [needLogin, setNeedLogin] = useState(false);
  
  // Booking options
  const [bookingType, setBookingType] = useState('hourly'); // hourly, package
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [hours, setHours] = useState(1);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [notes, setNotes] = useState('');
  
  // Result
  const [booking, setBooking] = useState(null);
  
  const loginUrl = `/login?redirect=${encodeURIComponent(location.pathname + (location.search || ''))}`;
  
  // Currency conversion hook
  const { formatPrice, convert, currency: userCurrency } = useCurrency();

  // Convert and format currency using user's preferred currency
  const formatAmount = (amount, sourceCurrency = 'SEK') => {
    const converted = convert(amount, sourceCurrency, userCurrency);
    return formatPrice(converted, userCurrency);
  };

  // Load service and related data
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const svcRes = await bookingService.fetchServiceById(serviceId);
        const svc = svcRes?.data || svcRes;
        if (!mounted) return;
        setService(svc);
        
        // Load creative
        if (svc?.creative_id) {
          try {
            const id = svc?.creative_id;
            const tRes = await bookingService.fetchCreativeById(id);
            if (mounted) setCreative(tRes?.data || tRes);
          } catch { void 0; }
        }
        
        // Load packages
        try {
          const pkgs = await bookingService.getServicePackages(serviceId);
          if (mounted) setPackages(pkgs || []);
        } catch { void 0; }
        
      } catch (e) {
        if (mounted) setError(e?.message || 'Failed to load service');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [serviceId]);

  // Handle payment redirect
  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    if (params.get('paid') === '1') {
      setStep(4);
      setBooking({ status: 'confirmed' });
    }
    if (params.get('canceled') === '1') {
      setError('Payment was cancelled. You can try again.');
    }
  }, [location.search]);

  // Calculate price
  const calculatePrice = () => {
    if (bookingType === 'package' && selectedPackage) {
      return {
        amount: selectedPackage.price || 0,
        currency: selectedPackage.currency || service?.currency || 'SEK',
      };
    }
    const hourlyRate = service?.hourly_rate || creative?.hourly_rate || 0;
    return {
      amount: hourlyRate * hours,
      currency: service?.currency || 'SEK',
    };
  };

  const price = calculatePrice();

  // Validate step
  const canProceed = () => {
    switch (step) {
      case 1:
        if (bookingType === 'package') return !!selectedPackage;
        return hours >= 1;
      case 2:
        return selectedDate && selectedTime;
      case 3:
        return true;
      default:
        return false;
    }
  };

  // Submit booking
  const handleSubmit = async () => {
    setError('');
    setSubmitting(true);

    try {
      // Check login
      let userId = null;
      try {
        const me = await apiGet('/users/me');
        userId = me?.data?.user?.id || me?.user?.id || me?.data?.id || me?.id || null;
      } catch { void 0; }

      if (!userId) {
        setError('Please login to book');
        setNeedLogin(true);
        setSubmitting(false);
        return;
      }

      // Build scheduled_at
      const scheduledAt = new Date(`${selectedDate}T${selectedTime}:00`).toISOString();

      // Use quick-pay for one-click booking + payment
      const res = await bookingService.quickPay({
        creative_id: service?.creative_id,
        service_id: service?.id,
        package_id: bookingType === 'package' ? selectedPackage?.id : undefined,
        meeting_type: bookingType,
        scheduled_at: scheduledAt,
        hours: bookingType === 'hourly' ? hours : undefined,
        notes: notes || undefined,
      });

      // If checkout URL provided, redirect to Stripe
      if (res?.checkout_url) {
        window.location.assign(res.checkout_url);
        return;
      }

      // If no payment required, show success
      if (!res?.payment_required) {
        setBooking(res?.booking);
        setStep(4);
      } else {
        setError(res?.message || 'Booking created but payment configuration needed.');
      }
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Failed to create booking');
    } finally {
      setSubmitting(false);
    }
  };

  // Generate time slots
  const timeSlots = [];
  for (let h = 9; h <= 17; h++) {
    timeSlots.push(`${String(h).padStart(2, '0')}:00`);
    timeSlots.push(`${String(h).padStart(2, '0')}:30`);
  }

  // Get minimum date (tomorrow)
  const minDate = new Date(Date.now() + 86400000).toISOString().slice(0, 10);

  if (loading) {
    return (
      <section className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      </section>
    );
  }

  if (!service) {
    return (
      <section className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <AlertCircle size={48} className="mx-auto text-red-500 mb-4" />
          <h2 className="text-xl font-semibold text-primary-text">Service Not Found</h2>
          <p className="text-secondary-text mt-2">The service you're looking for doesn't exist.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="container mx-auto px-4 py-8 max-w-3xl">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-primary-text mb-2">
          {t('bookingWizard.title', { defaultValue: 'Book Service' })}
        </h1>
        <p className="text-secondary-text">{service.title}</p>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-between mb-8">
        {['Select', 'Schedule', 'Review', 'Done'].map((label, idx) => {
          const stepNum = idx + 1;
          const isActive = step === stepNum;
          const isCompleted = step > stepNum;
          return (
            <div key={label} className="flex items-center flex-1">
              <div className="flex flex-col items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                    isActive
                      ? 'bg-blue-500 text-white'
                      : isCompleted
                      ? 'bg-green-500 text-white'
                      : 'bg-secondary-bg text-secondary-text border border-card-border'
                  }`}
                >
                  {isCompleted ? <CheckCircle size={20} /> : stepNum}
                </div>
                <span className={`text-xs mt-1 ${isActive ? 'text-blue-500 font-medium' : 'text-secondary-text'}`}>
                  {label}
                </span>
              </div>
              {idx < 3 && (
                <div className={`flex-1 h-0.5 mx-2 ${step > stepNum ? 'bg-green-500' : 'bg-card-border'}`} />
              )}
            </div>
          );
        })}
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-500 text-sm">
          {error}
        </div>
      )}

      {/* Step 1: Select Pricing */}
      {step === 1 && (
        <div className="card p-6 space-y-6">
          <h2 className="text-lg font-semibold text-primary-text flex items-center gap-2">
            <Package size={20} />
            Select Pricing Option
          </h2>

          {/* Booking Type Toggle */}
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => { setBookingType('hourly'); setSelectedPackage(null); }}
              className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                bookingType === 'hourly'
                  ? 'border-blue-500 bg-blue-500/5'
                  : 'border-card-border hover:border-blue-300'
              }`}
            >
              <Clock size={24} className={bookingType === 'hourly' ? 'text-blue-500 mb-2' : 'text-secondary-text mb-2'} />
              <div className="font-semibold text-primary-text">Hourly Rate</div>
              <div className="text-sm text-secondary-text">
                {formatPrice(convert(service.hourly_rate || creative?.hourly_rate || 0, service.currency || 'SEK', userCurrency), userCurrency)}/hr
              </div>
            </button>

            {packages.length > 0 && (
              <button
                type="button"
                onClick={() => setBookingType('package')}
                className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                  bookingType === 'package'
                    ? 'border-blue-500 bg-blue-500/5'
                    : 'border-card-border hover:border-blue-300'
                }`}
              >
                <Package size={24} className={bookingType === 'package' ? 'text-blue-500 mb-2' : 'text-secondary-text mb-2'} />
                <div className="font-semibold text-primary-text">Package</div>
                <div className="text-sm text-secondary-text">Fixed price packages</div>
              </button>
            )}
          </div>

          {/* Hourly Options */}
          {bookingType === 'hourly' && (
            <div className="space-y-3">
              <label className="block text-sm font-medium text-secondary-text">
                Number of Hours
              </label>
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={() => setHours(Math.max(1, hours - 1))}
                  className="w-10 h-10 rounded-lg border border-card-border bg-secondary-bg text-primary-text hover:bg-card-border"
                >
                  -
                </button>
                <span className="text-2xl font-bold text-primary-text w-12 text-center">{hours}</span>
                <button
                  type="button"
                  onClick={() => setHours(hours + 1)}
                  className="w-10 h-10 rounded-lg border border-card-border bg-secondary-bg text-primary-text hover:bg-card-border"
                >
                  +
                </button>
              </div>
            </div>
          )}

          {/* Package Options */}
          {bookingType === 'package' && packages.length > 0 && (
            <div className="space-y-3">
              <label className="block text-sm font-medium text-secondary-text">
                Select Package
              </label>
              <div className="grid gap-3">
                {packages.filter(p => p.status === 'active').map((pkg) => (
                  <button
                    key={pkg.id}
                    type="button"
                    onClick={() => setSelectedPackage(pkg)}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      selectedPackage?.id === pkg.id
                        ? 'border-blue-500 bg-blue-500/5'
                        : 'border-card-border hover:border-blue-300'
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-semibold text-primary-text">{pkg.name}</div>
                        {pkg.short_description && (
                          <div className="text-sm text-secondary-text mt-1">{pkg.short_description}</div>
                        )}
                        {pkg.delivery_days && (
                          <div className="text-xs text-secondary-text mt-2">
                            Delivery: {pkg.delivery_days} days
                          </div>
                        )}
                      </div>
                      <div className="text-lg font-bold text-primary-text">
                        {formatPrice(convert(pkg.price, pkg.currency || 'SEK', userCurrency), userCurrency)}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Price Summary */}
          <div className="flex items-center justify-between pt-4 border-t border-card-border">
            <span className="text-secondary-text">Total</span>
            <span className="text-2xl font-bold text-primary-text">
              {formatAmount(price.amount, price.currency)}
            </span>
          </div>

          {/* Actions */}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setStep(2)}
              disabled={!canProceed()}
              className="btn btn-primary flex items-center gap-2 disabled:opacity-50"
            >
              Continue <ChevronRight size={18} />
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Schedule */}
      {step === 2 && (
        <div className="card p-6 space-y-6">
          <h2 className="text-lg font-semibold text-primary-text flex items-center gap-2">
            <Calendar size={20} />
            Select Date & Time
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Date Picker */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-secondary-text">Date</label>
              <input
                type="date"
                value={selectedDate}
                min={minDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="input w-full"
              />
            </div>

            {/* Time Slots */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-secondary-text">Time</label>
              <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                {timeSlots.map((time) => (
                  <button
                    key={time}
                    type="button"
                    onClick={() => setSelectedTime(time)}
                    className={`py-2 px-3 rounded-lg border text-sm transition-all ${
                      selectedTime === time
                        ? 'border-blue-500 bg-blue-500/10 text-blue-600'
                        : 'border-card-border bg-secondary-bg text-primary-text hover:border-blue-300'
                    }`}
                  >
                    {time}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-secondary-text">
              Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any special requests or information..."
              className="input w-full h-24 resize-none"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-between">
            <button
              type="button"
              onClick={() => setStep(1)}
              className="btn btn-secondary"
            >
              Back
            </button>
            <button
              type="button"
              onClick={() => setStep(3)}
              disabled={!canProceed()}
              className="btn btn-primary flex items-center gap-2 disabled:opacity-50"
            >
              Continue <ChevronRight size={18} />
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Review */}
      {step === 3 && (
        <div className="card p-6 space-y-6">
          <h2 className="text-lg font-semibold text-primary-text flex items-center gap-2">
            <CreditCard size={20} />
            Review & Pay
          </h2>

          {/* Creative Info */}
          {creative && (
            <div className="flex items-center gap-4 pb-4 border-b border-card-border">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
                {creative.avatar_url ? (
                  <img src={creative.avatar_url} alt="" className="w-12 h-12 rounded-full object-cover" />
                ) : (
                  <User size={24} className="text-white" />
                )}
              </div>
              <div>
                <div className="font-medium text-primary-text">{creative.display_name || creative.full_name}</div>
                <div className="text-sm text-secondary-text">{creative.headline || creative.title}</div>
              </div>
            </div>
          )}

          {/* Booking Details */}
          <div className="space-y-3">
            <div className="flex justify-between py-2">
              <span className="text-secondary-text">Service</span>
              <span className="text-primary-text font-medium">{service.title}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-secondary-text">Type</span>
              <span className="text-primary-text">
                {bookingType === 'package' ? `Package: ${selectedPackage?.name}` : `${hours} hour(s)`}
              </span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-secondary-text">Date</span>
              <span className="text-primary-text">
                {selectedDate ? new Date(selectedDate).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : '-'}
              </span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-secondary-text">Time</span>
              <span className="text-primary-text">{selectedTime || '-'}</span>
            </div>
            {notes && (
              <div className="py-2">
                <span className="text-secondary-text block mb-1">Notes</span>
                <span className="text-primary-text text-sm">{notes}</span>
              </div>
            )}
            <div className="flex justify-between py-3 border-t border-card-border">
              <span className="text-lg font-semibold text-primary-text">Total</span>
              <span className="text-2xl font-bold text-primary-text flex items-center gap-1">
                <Coins size={20} />
                {formatAmount(price.amount, price.currency)}
              </span>
            </div>
          </div>

          {/* Payment Notice */}
          <div className="bg-blue-500/5 border border-blue-200 rounded-lg p-4 text-sm text-secondary-text">
            <p>You will be redirected to Stripe to complete your payment securely.</p>
          </div>

          {needLogin && (
            <button
              type="button"
              onClick={() => navigate(loginUrl)}
              className="w-full btn btn-secondary"
            >
              Log in to continue
            </button>
          )}

          {/* Actions */}
          <div className="flex justify-between">
            <button
              type="button"
              onClick={() => setStep(2)}
              className="btn btn-secondary"
            >
              Back
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              className="btn btn-primary flex items-center gap-2 disabled:opacity-50"
            >
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard size={18} />
                  Pay {formatAmount(price.amount, price.currency)}
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Success */}
      {step === 4 && (
        <div className="card p-8 text-center space-y-6">
          <div className="w-20 h-20 mx-auto bg-green-500/10 rounded-full flex items-center justify-center">
            <CheckCircle size={40} className="text-green-500" />
          </div>
          <h2 className="text-2xl font-bold text-primary-text">Booking Confirmed!</h2>
          <p className="text-secondary-text">
            Your booking has been confirmed and payment processed successfully.
          </p>
          {booking?.booking_number && (
            <div className="text-sm text-secondary-text">
              Booking #: <span className="font-mono font-medium">{booking.booking_number}</span>
            </div>
          )}
          <div className="flex justify-center gap-4">
            <button
              type="button"
              onClick={() => navigate('/dashboard/bookings')}
              className="btn btn-primary"
            >
              View My Bookings
            </button>
            <button
              type="button"
              onClick={() => navigate('/creatives')}
              className="btn btn-secondary"
            >
              Browse More Creatives
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
